import { ReactComponent as PlaylistIcon } from 'resources/images/playlist.svg';
import {
  Center,
  Flex,
  Icon,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { PlaylistImage, PlaylistImageValidations } from 'types/playlists.types';
import { UploadImageModal } from 'app/components/Core';
import {
  deletePlaylistImageStatusEffect,
  fetchPlaylistImageStatusEffect,
  uploadPlaylistImageEffect,
} from 'app/store/Playlists/effects';
import { useImageUploadPolling } from 'app/components/Profile/ProfileImage/hooks';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useFetchValidationSchema } from 'app/utils/hooks';
import { getUploadPlaylistImageLink } from 'app/store/Playlists/selectors';
import { ChakraConfirmationDialog } from 'app/components/Dialogs';
import { playlistHeaderImageClickedAction } from 'app/store/Mixpanel/slice';
import { ShareResponse } from 'app/components/Shares/types';
import { SongCover } from 'app/components/Song/SongCover';
import { useNavigate } from 'react-router-dom';

interface PlaylistHeaderImageProps {
  playlistGuid: string;
  images: PlaylistImage[];
  song?: ShareResponse['response']['song']['item'];
  to?: string;
  readOnly?: boolean;
}

export const PlaylistHeaderImage = ({
  images,
  playlistGuid,
  song,
  to,
  readOnly = false,
}: PlaylistHeaderImageProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const playlistImage = useMemo(
    () => images.find((image) => image.name === 'playlist-small'),
    [images]
  );
  const startPolling = useImageUploadPolling(() =>
    dispatch(fetchPlaylistImageStatusEffect()).unwrap()
  );
  const link = useAppSelector(getUploadPlaylistImageLink);
  const {
    validationSchema,
  } = useFetchValidationSchema<PlaylistImageValidations>({
    link,
  });

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deletePlaylistImageStatusEffect(playlistGuid));
      setDeleteOpen(false);
    } catch (e) {
      // proceed
    } finally {
      setIsDeleting(false);
    }
  };

  if (readOnly) {
    return song ? (
      <SongCover status={song.status} />
    ) : (
      <Image
        src={playlistImage?.href}
        {...(to ? { role: 'button', onClick: () => navigate(to) } : {})}
        alt="playlist cover"
        width="128px"
        height="128px"
        fallback={
          <Center bg="M.20">
            <Icon as={PlaylistIcon} color="P.40" width="128px" height="128px" />
          </Center>
        }
      />
    );
  }

  return (
    <>
      <UploadImageModal
        title="Cover Image"
        open={imageUploadOpen}
        onClose={() => setImageUploadOpen(false)}
        startPolling={startPolling}
        effect={uploadPlaylistImageEffect}
        minAllowedDimension={
          validationSchema?.properties.cropOptions.properties.width.minimum
        }
      />
      <ChakraConfirmationDialog
        isOpen={deleteOpen}
        title="Remove Image"
        message="Are you sure you want to remove the current cover image?"
        actionButtonText="Delete"
        handleAction={handleDelete}
        handleClose={() => setDeleteOpen(false)}
        disableActions={isDeleting}
      />
      <Menu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        placement="bottom"
        offset={[60, 16]}
      >
        <Flex
          position="relative"
          rounded={4}
          overflow="hidden"
          role="group"
          width="128px"
          height="128px"
          minWidth="128px"
          minHeight="128px"
          cursor="pointer"
          onClick={() => {
            dispatch(playlistHeaderImageClickedAction(playlistGuid));
            if (playlistImage) {
              setMenuOpen(true);
              return;
            }
            setImageUploadOpen(true);
          }}
        >
          <Image
            src={playlistImage?.href}
            alt="playlist cover"
            width="128px"
            height="128px"
            fallback={
              <Center bg="M.20">
                <Icon
                  as={PlaylistIcon}
                  color="P.40"
                  width="128px"
                  height="128px"
                  p={6}
                />
              </Center>
            }
          />
          <Flex
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height="30px"
            backgroundColor="surface1"
            justifyContent="center"
            alignItems="center"
            opacity={menuOpen ? 1 : 0}
            _groupHover={{
              opacity: 1,
            }}
          >
            <MenuButton>
              <Text
                color="white"
                fontWeight={600}
                fontSize="xxs"
                textTransform="uppercase"
                letterSpacing="0.8px"
              >
                {playlistImage ? 'Edit Image' : 'Add Image'}
              </Text>
            </MenuButton>
          </Flex>
        </Flex>
        <MenuList>
          <MenuItem onClick={() => setImageUploadOpen(true)}>
            Change image
          </MenuItem>
          <MenuItem onClick={() => setDeleteOpen(true)} color="R.80">
            Remove image
          </MenuItem>
        </MenuList>
      </Menu>
    </>
  );
};
